/**
 * ProofCanvas - the exact "As Word" preview, rendered ENTIRELY IN THE BROWSER.
 * The current draft contract + sample + brand are rendered to a .docx Blob via the
 * isomorphic docx packer (lazy-loaded) and shown with docx-preview - the same
 * renderer the server export uses, so Proof can't diverge from what ships.
 * Read-only; re-renders debounced (CPU, not network) and cached by input hash.
 */

import type { DocumentContract } from '@protoss/doc';
import * as React from 'react';
import { DocxBlobPreview } from '../../../../components/doc/DocxBlobPreview';
import type { PreviewSample } from '../../../../components/doc';
import { buildStudioRenderContext, fetchAssetBuffers } from './sowProofRender';

const RENDER_DEBOUNCE_MS = 400;

export interface ProofCanvasProps {
  contract: DocumentContract;
  sample: PreviewSample;
  assetSlots: Record<string, string>;
  brandColor: string | null;
}

export function ProofCanvas({ contract, sample, assetSlots, brandColor }: ProofCanvasProps) {
  const [blob, setBlob] = React.useState<Blob | null>(null);
  const [error, setError] = React.useState(false);
  // Re-render only when the inputs actually change (debounced for CPU).
  const key = React.useMemo(
    () => JSON.stringify([contract, sample, assetSlots, brandColor]),
    [contract, sample, assetSlots, brandColor]
  );

  React.useEffect(() => {
    let cancelled = false;
    setError(false);
    const timer = window.setTimeout(() => void render(), RENDER_DEBOUNCE_MS);
    async function render() {
      try {
        const assets = await fetchAssetBuffers(assetSlots);
        if (cancelled) return;
        const ctx = buildStudioRenderContext(sample, brandColor, assets);
        // Lazy-load the docx generator so it stays out of the main bundle.
        const { renderDocumentContractToBlob } = await import('@protoss/doc');
        if (cancelled) return;
        const res = await renderDocumentContractToBlob(contract, ctx);
        if (!cancelled) setBlob(res.blob);
      } catch {
        if (!cancelled) setError(true);
      }
    }
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // `key` captures contract/sample/assetSlots/brandColor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return (
    <div className="sow-studio-proof">
      <div className="sow-studio-proof__hint">Exact Word preview - switch to Design to edit.</div>
      {error ? (
        <p className="sow-studio-proof__error">
          Couldn&apos;t generate the exact preview. Switch back to Design.
        </p>
      ) : (
        <DocxBlobPreview blob={blob} fit={false} />
      )}
    </div>
  );
}
